import type { AgentPatch, EditorCommandPatch, EntityMetadataPatch, SlateOpsPatch } from '@tunasong/agent-tools'
import { composeOpPath, findBlockPath, validateSlateOpsAgainstTree } from '@tunasong/agent-tools/slate-path'
import { getAgentCommandApplicator } from '@tunasong/plugin-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { Entity } from '@tunasong/schemas'
import { resolveAnchor } from './resolve-anchor.js'

type EditorOperation = Parameters<TunaEditor['apply']>[0]

/**
 * Apply a slate-ops patch. When `patch.anchor` is set, resolve `anchor.id`
 * to its current Slate path, append `anchor.subPath` (if any), and
 * prepend the resulting base path to every op's `path` / `newPath` before
 * `editor.apply`. Stale anchor (`anchor.id` no longer in the tree) →
 * throws so the proposal UI flips to a Reject-only stale state.
 *
 * When `anchor` is absent, ops apply at document root (backward compat).
 */
export const applySlateOps = (editor: TunaEditor, patch: SlateOpsPatch) => {
  const tree = editor as unknown as { children?: unknown[] }
  // Pre-flight: validate EVERY composed path before applying ANY op, so an
  // invalid patch no-ops instead of half-mutating the document.
  const validation = validateSlateOpsAgainstTree(tree, {
    anchor: patch.anchor,
    ops: patch.ops as Record<string, unknown>[],
  })
  if (!validation.ok) {
    throw new Error(`slate-ops apply rejected: ${validation.reason}`)
  }
  let base: number[] = []
  if (patch.anchor) {
    const found = findBlockPath(tree, patch.anchor.id)
    // validation above guarantees found is defined; guard for type-narrowing.
    if (!found) {
      throw new Error(`slate-ops anchor block not found: ${patch.anchor.id}`)
    }
    base = [...found.path, ...(patch.anchor.subPath ?? [])]
  }
  for (const op of patch.ops) {
    const rewritten = composeOpPath(op as Record<string, unknown>, base)
    editor.apply(rewritten as unknown as EditorOperation)
  }
}

export const buildMetadataUpdate = (patch: EntityMetadataPatch): Partial<Entity> => ({ ...patch.fields })

/**
 * Apply an editor-command proposal: walk the live tree to resolve the anchor,
 * then invoke the plugin-registered applicator with the resolved path and args.
 *
 * Stale anchor (`anchor.id` no longer in the tree) → throws so the UI can
 * flip the proposal to a Reject-only stale state rather than mutating the
 * wrong target. Unknown commandId (no applicator registered) → throws,
 * which should not happen in production because run_editor_command also
 * gates on the registry server-side.
 */
export const applyEditorCommand = (editor: TunaEditor, patch: EditorCommandPatch): void => {
  const resolved = resolveAnchor(editor, patch.anchor)
  if ('error' in resolved) {
    throw new Error(resolved.error)
  }
  const applicator = getAgentCommandApplicator(patch.commandId)
  if (!applicator) {
    throw new Error(`No applicator registered for command: ${patch.commandId}`)
  }
  applicator(editor, resolved.path, patch.args ?? {})
}

export interface ApplyProposalDeps {
  editor?: TunaEditor | null
  updateEntity: (entityId: string, partialEntity: Partial<Entity>) => void
}

export const applyProposal = (proposal: { targetEntityId: string; patch: AgentPatch }, deps: ApplyProposalDeps) => {
  const { patch } = proposal
  if (patch.kind === 'slate-ops') {
    if (!deps.editor) {
      throw new Error('Cannot apply slate-ops proposal: editor is not mounted')
    }
    if (deps.editor.rootElement?.id && deps.editor.rootElement.id !== proposal.targetEntityId) {
      throw new Error(`Proposal targets entity ${proposal.targetEntityId} but editor has ${deps.editor.rootElement.id}`)
    }
    applySlateOps(deps.editor, patch)
    return
  }
  if (patch.kind === 'entity-metadata') {
    deps.updateEntity(proposal.targetEntityId, buildMetadataUpdate(patch))
    return
  }
  if (patch.kind === 'editor-command') {
    if (!deps.editor) {
      throw new Error('Cannot apply editor-command proposal: editor is not mounted')
    }
    applyEditorCommand(deps.editor, patch)
    return
  }
  const exhaustive: never = patch
  throw new Error(`Unknown patch kind: ${JSON.stringify(exhaustive)}`)
}
